import { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { Page, PageProps } from 'react-pdf';
import { PDFPageProxy } from 'pdfjs-dist';

import useIntersectionObserver from '../../hooks/useIntersectionObserver';

type PDFPageProps  =  PageProps  & {
    onInViewPortChange?: Function;
    scrollIntoView?: boolean;
    parentElement?: Element;
    contentToHighlight?: string;
    rotateSteps?: number;
    page?: PDFPageProxy;
    style?: React.CSSProperties;
}

const PDFPage: FunctionComponent<PDFPageProps> = ({
    onInViewPortChange,
    scrollIntoView,
    parentElement,
    scale = 1,
    contentToHighlight = '',
    rotateSteps = 0,
    page,
    style,
    ...props
}) => {
    const ref = useRef<HTMLDivElement>();

    const rotateDegree = useMemo(
        () => {
          const deg = (page?.rotate || 0) + rotateSteps * 90;
          if (deg === -90) {
              return 270;
          }
          if (deg === -270) {
              return 90;
          }
          return Math.abs(deg) % 360;
        },
        [page, rotateSteps]
    );

    const threshold = useMemo(() =>  Math.max(0, 1 - scale / 2.5), [scale]);

    const observerEntry = useIntersectionObserver(ref, {
        root: parentElement,
        threshold
    });

    useEffect(() => {
        if (observerEntry) {
            onInViewPortChange?.(observerEntry.isIntersecting, page.pageNumber);
        }
    }, [observerEntry?.isIntersecting, page.pageNumber]);


    return (
        <div className={`pdf-page`} style={style}>
            <Page
                key={`page_${page.pageNumber}`}
                pageNumber={page.pageNumber}
                {...props}
                inputRef={ref}
                rotate={rotateDegree}
                scale={scale}
            />
        </div>
    );
};

export default PDFPage;
